// src/utilities/iframeResizer.ts

// Configuration constants
const DEFAULT_EVENT_TYPE = "IFRAME_RESIZE";
const DEFAULT_HEIGHT_THRESHOLD = 5;
const THROTTLE_DELAY_MS = 100;
const WILDCARD_ORIGIN = "*";

// Types
interface IframeResizerOptions {
  eventType?: string;
  threshold?: number;
}

interface ResizeMessage {
  type: string;
  height: number;
}

interface HeightTracker {
  lastSentHeight: number;
  lastMessageSentTime: number;
  throttleTimer?: number;
}

/**
 * Calculates the current document height using the most reliable method
 * @returns The maximum height between body and document element
 */
function getCurrentDocumentHeight(): number {
  return Math.max(
    document.body.offsetHeight,
    document.documentElement.offsetHeight
  );
}

/**
 * Creates a function that sends height updates to the parent window
 * @param eventType The event type to send in the message
 * @param threshold The minimum height difference required to trigger an update
 * @param tracker Object to track height and timing state
 * @returns Function that sends height updates when threshold is exceeded
 */
function createHeightUpdateSender(
  eventType: string,
  threshold: number,
  tracker: HeightTracker
) {
  return function sendHeightUpdateToParent(): void {
    try {
      const currentHeight = getCurrentDocumentHeight();
      const heightDifference = Math.abs(currentHeight - tracker.lastSentHeight);
      
      if (heightDifference > threshold) {
        const message: ResizeMessage = {
          type: eventType,
          height: currentHeight
        };
        
        tracker.lastSentHeight = currentHeight;
        tracker.lastMessageSentTime = Date.now();
        window.parent.postMessage(message, WILDCARD_ORIGIN);
      }
    } catch {
      // Fail silently if postMessage is not available or fails
    }
  };
}

/**
 * Creates a throttled version of the height update function
 * @param sendHeightUpdate The function to throttle
 * @param tracker Object to track timing state and throttle timer
 * @returns Throttled function that prevents excessive messaging
 */
function createThrottledHeightUpdate(
  sendHeightUpdate: () => void,
  tracker: HeightTracker
) {
  return function throttledHeightUpdate(): void {
    const currentTime = Date.now();
    const timeSinceLastMessage = currentTime - tracker.lastMessageSentTime;
    
    if (timeSinceLastMessage >= THROTTLE_DELAY_MS) {
      // Enough time has passed, send immediately
      sendHeightUpdate();
    } else {
      // Clear any existing timer to avoid duplicate scheduled calls
      if (tracker.throttleTimer !== undefined) {
        clearTimeout(tracker.throttleTimer);
      }
      
      // Schedule to send after the remaining delay
      const remainingDelay = THROTTLE_DELAY_MS - timeSinceLastMessage;
      tracker.throttleTimer = window.setTimeout(() => {
        tracker.throttleTimer = undefined;
        sendHeightUpdate();
      }, remainingDelay);
    }
  };
}

/**
 * Utility for automatically resizing iframes by sending height updates to the parent window.
 * 
 * This function sets up observers to detect changes in the document height and 
 * communicates these changes to the parent window via postMessage.
 * 
 * Features:
 * - Throttled height updates to prevent excessive messaging
 * - Multiple detection methods: ResizeObserver, MutationObserver, and window resize events
 * - Configurable height change threshold to avoid minor fluctuations
 * - Automatic cleanup function returned for proper teardown
 * 
 * @param options Configuration options for the iframe resizer
 * @returns Cleanup function to remove all observers and event listeners
 */
export function setupIframeResizer(options: IframeResizerOptions = {}): () => void {
  const { eventType = DEFAULT_EVENT_TYPE, threshold = DEFAULT_HEIGHT_THRESHOLD } = options;
  
  // State tracker for height and timing information
  const tracker: HeightTracker = {
    lastSentHeight: 0,
    lastMessageSentTime: 0,
    throttleTimer: undefined
  };

  // Create the height update functions
  const sendHeightUpdate = createHeightUpdateSender(eventType, threshold, tracker);
  const throttledHeightUpdate = createThrottledHeightUpdate(sendHeightUpdate, tracker);

  /**
   * Sets up observers and event listeners to monitor document changes
   * @returns Cleanup function to remove all observers and listeners
   */
  function setupObserversAndListeners(): () => void {
    let resizeObserver: ResizeObserver | undefined;
    
    // Use ResizeObserver if available for more efficient monitoring
    if ("ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(throttledHeightUpdate);
      resizeObserver.observe(document.body);
    }
    
    // Set up MutationObserver to detect DOM changes
    const mutationObserver = new MutationObserver(throttledHeightUpdate);
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
      // Note: Attributes monitoring is commented out for performance
      // attributes: true,
      // attributeFilter: ["style", "class"],
    });
    
    // Listen for window resize events as a fallback
    window.addEventListener("resize", throttledHeightUpdate);
    
    // Send initial height immediately
    sendHeightUpdate();

    // Return cleanup function
    return () => {
      window.removeEventListener("resize", throttledHeightUpdate);
      mutationObserver.disconnect();
      
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      
      if (tracker.throttleTimer !== undefined) {
        clearTimeout(tracker.throttleTimer);
        tracker.throttleTimer = undefined;
      }
    };
  }

  return setupObserversAndListeners();
}
